// 근무스케줄 관리 페이지 (관리자 전용)
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Grid,
  Text,
  useColorModeValue,
  useToast,
  Heading,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useDisclosure,
  FormControl,
  FormLabel,
  Select
} from '@chakra-ui/react';
import { AddIcon, CalendarIcon, BellIcon } from '@chakra-ui/icons';
import Card from 'components/Card/Card';
import CardHeader from 'components/Card/CardHeader';
import CardBody from 'components/Card/CardBody';
import { useAuth } from 'contexts/AuthContext';
import {
  getWorkSchedules,
  getScheduleApplications,
  getLocations,
  getAllSemesters
} from 'utils/firestoreUtils';
import { getDateRange } from 'utils/scheduleUtils';
import moment from 'moment';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import SlotCreationModal from './components/SlotCreationModal';
import ApplicationList from './components/ApplicationList';
import BulkScheduleModal from './components/BulkScheduleModal';
import ScheduleAlimtalkModal from './components/ScheduleAlimtalkModal';

const localizer = momentLocalizer(moment);

function ScheduleManagement() {
  const { currentUser } = useAuth();
  const toast = useToast();
  const textColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  
  const { 
    isOpen: isSlotModalOpen, 
    onOpen: onSlotModalOpen, 
    onClose: onSlotModalClose 
  } = useDisclosure();
  
  const { 
    isOpen: isAppListOpen, 
    onOpen: onAppListOpen, 
    onClose: onAppListClose 
  } = useDisclosure();

  const { 
    isOpen: isBulkModalOpen, 
    onOpen: onBulkModalOpen, 
    onClose: onBulkModalClose 
  } = useDisclosure();

  const [schedules, setSchedules] = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(getDateRange(0, 60));
  const [locations, setLocations] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [selectedSemester, setSelectedSemester] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  
  const { 
    isOpen: isAlimtalkOpen, 
    onOpen: onAlimtalkOpen, 
    onClose: onAlimtalkClose 
  } = useDisclosure();

  useEffect(() => {
    if (currentUser) {
      loadData();
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser && selectedSemester) {
      loadSchedules();
    }
  }, [dateRange, selectedLocation, selectedSemester]);

  const loadData = async () => {
    try {
      const [locs, sems] = await Promise.all([
        getLocations(),
        getAllSemesters()
      ]);
      setLocations(locs);
      setSemesters(sems);
    } catch (error) {
      console.error('데이터 조회 오류:', error);
    }
  };

  const loadSchedules = async () => {
    if (!selectedSemester) {
      setSchedules([]);
      return;
    }

    try {
      setLoading(true);
      setHasSearched(true);
      const data = await getWorkSchedules({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        location: selectedLocation || undefined
      });
      setSchedules(data);
    } catch (error) {
      console.error('스케줄 조회 오류:', error);
      toast({
        title: '조회 실패',
        description: '스케줄을 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNewSchedule = () => {
    setEditingSchedule(null);
    onSlotModalOpen();
  };

  const handleEditSchedule = (schedule) => {
    setEditingSchedule(schedule);
    onSlotModalOpen();
  };

  const handleScheduleSaved = () => {
    loadSchedules();
    onSlotModalClose();
  };

  const handleEventClick = (event) => {
    setSelectedSchedule(event.resource);
    onAppListOpen();
  };

  const handleCopySchedule = (schedule) => {
    setEditingSchedule({
      ...schedule,
      id: null, // 복사이므로 ID 제거
      workDate: '', // 날짜는 새로 선택
      confirmedCount: 0,
      status: 'open'
    });
    onSlotModalOpen();
  };

  // 캘린더 이벤트 변환
  const events = schedules.map(schedule => {
    const date = new Date(schedule.workDate);
    const [startHour, startMinute] = schedule.startTime.split(':').map(Number);
    const [endHour, endMinute] = schedule.endTime.split(':').map(Number);
    
    const start = new Date(date);
    start.setHours(startHour, startMinute, 0);
    
    const end = new Date(date);
    end.setHours(endHour, endMinute, 0);

    return {
      title: `${schedule.location} (${schedule.confirmedCount || 0}/${schedule.requiredHeadcount}) ${schedule.startTime}~${schedule.endTime}`,
      start,
      end,
      resource: schedule
    };
  });

  const eventStyleGetter = (event) => {
    const schedule = event.resource;
    let backgroundColor = '#3182ce'; // blue
    
    if (schedule.status === 'closed') {
      backgroundColor = '#38a169'; // green
    } else if (schedule.status === 'completed') {
      backgroundColor = '#718096'; // gray
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 0.8,
        color: 'white',
        border: '0px',
        display: 'block'
      }
    };
  };

  return (
    <Flex direction="column" pt={{ base: '120px', md: '75px' }}>

      {/* 헤더 영역 */}
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color={textColor} mb={1}>
            근무스케줄 관리
          </Heading>
        </Box>
        <Flex gap={3}>
          <Button
            colorScheme="green"
            variant="outline"
            onClick={onBulkModalOpen}
            isLoading={loading}
          >
            일괄 등록
          </Button>
          <Button
            colorScheme="purple"
            variant="outline"
            leftIcon={<BellIcon />}
            onClick={onAlimtalkOpen}
            isDisabled={!selectedSemester}
          >
            알림톡 발송
          </Button>
          <Button
            colorScheme="blue"
            leftIcon={<AddIcon />}
            onClick={handleNewSchedule}
            isLoading={loading}
          >
            스케줄 추가
          </Button>
        </Flex>
      </Flex>

      {/* 필터 영역 */}
      <Card mb={6}>
        <CardBody py={4}>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="bold">학기 선택 (필수)</FormLabel>
              <Select
                value={selectedSemester?.id || ''}
                onChange={(e) => {
                  const semester = semesters.find(s => s.id === e.target.value);
                  setSelectedSemester(semester);
                  setSelectedLocation('');
                  if (semester) {
                    setDateRange({
                      startDate: semester.startDate,
                      endDate: semester.endDate
                    });
                  } else {
                    setDateRange(getDateRange(0, 60));
                    setHasSearched(false);
                  }
                }}
                placeholder="학기를 선택하세요"
              >
                {semesters.map(sem => (
                  <option key={sem.id} value={sem.id}>
                    {sem.semesterName} ({sem.startDate} ~ {sem.endDate})
                  </option>
                ))}
              </Select>
              {!selectedSemester && (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  학기를 선택하면 해당 기간의 스케줄이 조회됩니다
                </Text>
              )}
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">근무지 필터</FormLabel>
              <Select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                placeholder="전체 근무지"
                isDisabled={!selectedSemester}
              >
                {selectedSemester?.workplaces?.map(loc => (
                  <option key={loc} value={loc}>{loc}</option>
                )) || locations.map(loc => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </CardBody>
      </Card>

      {/* 캘린더 */}
      <Card>
        <CardBody>
          {!hasSearched ? (
            <Box textAlign="center" py={20}>
              <CalendarIcon boxSize={16} color="gray.300" mb={4} />
              <Text color="gray.500" fontSize="lg" fontWeight="medium">
                학기를 선택하여 스케줄을 조회하세요
              </Text>
              <Text fontSize="sm" color="gray.400" mt={2}>
                위의 필터에서 학기를 선택하면 해당 기간의 근무 스케줄이 표시됩니다
              </Text>
            </Box>
          ) : (
          <Box width="100%">
          <Box 
            height="calc(100vh - 350px)" 
            minHeight="900px"
            width="100%"
            sx={{
              '.rbc-calendar': {
                height: '100%',
                fontFamily: 'inherit'
              },
              '.rbc-month-row': {
                minHeight: '140px',
                overflow: 'visible'
              },
              '.rbc-header': {
                padding: '10px 3px',
                fontWeight: 'bold',
                fontSize: '14px',
                borderBottom: '2px solid',
                borderColor: borderColor
              },
              '.rbc-today': {
                backgroundColor: useColorModeValue('blue.50', 'blue.900')
              },
              '.rbc-event': {
                padding: '6px 6px',
                borderRadius: '6px',
                fontSize: '12px',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s'
              },
              '.rbc-event:hover': {
                opacity: 0.9,
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
              },
              '.rbc-toolbar': {
                padding: '15px 0',
                marginBottom: '15px',
                fontSize: '20px',
                fontWeight: 'bold',
                flexWrap: 'wrap',
                gap: '10px'
              },
              '.rbc-toolbar button': {
                color: textColor,
                padding: '8px 15px',
                borderRadius: '6px',
                border: '1px solid',
                borderColor: borderColor,
                backgroundColor: 'white',
                fontSize: '14px',
                fontWeight: '500'
              },
              '.rbc-toolbar button:hover': {
                backgroundColor: useColorModeValue('gray.100', 'gray.700')
              },
              '.rbc-toolbar button.rbc-active': {
                backgroundColor: 'blue.500',
                color: 'white',
                borderColor: 'blue.500'
              },
              '.rbc-month-view': {
                border: '1px solid',
                borderColor: borderColor,
                borderRadius: '8px',
                overflow: 'hidden'
              },
              '.rbc-day-bg': {
                borderColor: borderColor
              },
              '.rbc-off-range-bg': {
                backgroundColor: useColorModeValue('gray.50', 'gray.800')
              },
              '.rbc-date-cell': {
                padding: '8px',
                fontSize: '14px',
                fontWeight: '500'
              },
              '.rbc-show-more': {
                color: 'blue.500',
                fontWeight: '600',
                fontSize: '12px',
                marginTop: '4px'
              },
              '.rbc-label': {
                padding: '10px 3px',
                fontWeight: 'bold',
                fontSize: '14px',
              },
            }}
          >
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: '100%', width: '100%' }}
              onSelectEvent={handleEventClick}
              eventPropGetter={eventStyleGetter}
              views={['month', 'week', 'day']}
              max={3}
              messages={{
                next: '다음',
                previous: '이전',
                today: '오늘',
                month: '월간',
                week: '주간',
                day: '일간',
                showMore: (total) => `+${total} 더보기`
              }}
            />
          </Box>

          {/* 범례 */}
          <Box mt={6}>
            <Flex gap={6} fontSize="sm" flexWrap="wrap" justify="center">
              <Flex align="center" gap={2}>
                <Box w="16px" h="16px" bg="blue.500" borderRadius="4px" />
                <Text fontWeight="500">모집중</Text>
              </Flex>
              <Flex align="center" gap={2}>
                <Box w="16px" h="16px" bg="green.500" borderRadius="4px" />
                <Text fontWeight="500">마감</Text>
              </Flex>
              <Flex align="center" gap={2}>
                <Box w="16px" h="16px" bg="gray.500" borderRadius="4px" />
                <Text fontWeight="500">완료</Text>
              </Flex>
            </Flex>
          </Box>
          </Box>
          )}
        </CardBody>
      </Card>

      {/* 슬롯 생성/수정 모달 */}
      <SlotCreationModal
        isOpen={isSlotModalOpen}
        onClose={onSlotModalClose}
        editingSchedule={editingSchedule}
        onSaved={handleScheduleSaved}
      />

      {/* 일괄 등록 모달 */}
      <BulkScheduleModal
        isOpen={isBulkModalOpen}
        onClose={onBulkModalClose}
        onSaved={handleScheduleSaved}
      />

      {/* 알림톡 발송 모달 */}
      <ScheduleAlimtalkModal
        isOpen={isAlimtalkOpen}
        onClose={onAlimtalkClose}
        selectedSemester={selectedSemester}
      />

      {/* 신청자 목록 드로어 */}
      <ApplicationList
        isOpen={isAppListOpen}
        onClose={onAppListClose}
        schedule={selectedSchedule}
        onUpdate={loadSchedules}
        onCopy={handleCopySchedule}
      />
    </Flex>
  );
}

export default ScheduleManagement;
